import { useMemo, useState } from "react";
import { QuizDetailsDialogProps } from "./props.type";
import { QuizSubmissionAnswerDto } from "@/services/challenges/types/challenges.types";
import { toast } from "react-toastify";
import { submitQuizChallenge } from "@/services/challenges/quizChallenges";
import { invalidateRegistrationProgressQueries } from "@/services/inscription/invalidateRegistrationProgressQueries";
import { queryClient } from "@/services/react-query";
import { useLoader } from "@/context/LoaderContext";

export const useQuizQuestionsDialog = ({
  challenge,
  onClose,
}: Partial<QuizDetailsDialogProps>) => {
  const loader = useLoader();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<QuizSubmissionAnswerDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [isOpenLeaveDialog, setIsOpenLeaveDialog] = useState(false);
  const toggleLeaveDialog = () => setIsOpenLeaveDialog(!isOpenLeaveDialog);

  const currentQuestion = useMemo(() => {
    return challenge?.questions?.[currentQuestionIndex];
  }, [challenge?.questions, currentQuestionIndex]);

  const currentAlternatives = useMemo(() => {
    return currentQuestion?.alternatives ?? [];
  }, [currentQuestion]);

  const currentAnswer = useMemo(() => {
    if (!currentQuestion) return;

    return answers.find((a) => a.questionId === currentQuestion.id);
  }, [currentQuestion, answers]);

  const isLastQuestion = useMemo(
    () => currentQuestionIndex >= (challenge?.questions?.length ?? 0) - 1,
    [currentQuestionIndex, challenge?.questions]
  );

  const setCurrentQuestionAnswer = (questionAlternativeId: number) => {
    if (!currentQuestion) return;

    const filteredAnswers = answers.filter(
      ({ questionId }) => questionId !== currentQuestion.id
    );
    setAnswers([
      ...filteredAnswers,
      { questionId: currentQuestion.id, questionAlternativeId },
    ]);
  };

  const getIsSelected = (id: number) => {
    return currentAnswer?.questionAlternativeId === id;
  };

  const onLeaveQuiz = () => {
    toggleLeaveDialog();
    onClose?.();

    setAnswers([]);
    setCurrentQuestionIndex(0);
  };

  const onPreviousQuestion = () =>
    setCurrentQuestionIndex(currentQuestionIndex - 1);

  const onNextQuestion = () => {
    setCurrentQuestionIndex(currentQuestionIndex + 1);
  };

  const onSubmit = async () => {
    try {
      if (!challenge?.id) return;

      if (challenge?.status === "Finished") {
        return;
      }
      loader.show("submit-quiz-challenge");
      setIsLoading(true);

      await submitQuizChallenge({ id: challenge?.id, answers });

      await queryClient.invalidateQueries({
        queryKey: ["stage-challenges", challenge?.stageId],
      });
      await queryClient.invalidateQueries({
        queryKey: "challenge-details",
        exact: false,
      });
      await invalidateRegistrationProgressQueries();
    } catch (err) {
      console.error("Error submitting challenge", err);
      toast("Erro ao enviar desafio", { type: "error" });
    } finally {
      setIsLoading(false);
      loader.hide("submit-quiz-challenge");
    }
  };

  return {
    currentQuestionIndex,
    currentQuestion,
    isLastQuestion,

    currentAlternatives,
    currentAnswer,
    answers,
    getIsSelected,
    setCurrentQuestionAnswer,

    isOpenLeaveDialog,
    toggleLeaveDialog,

    onLeaveQuiz,
    onPreviousQuestion,
    onNextQuestion,
    onSubmit,
    isLoading,
  };
};
